// Vitrine publica /catalogo — sem login. Mobile-first.
// Onda Catalogo Publico Fase 1b.
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, MessageCircle } from 'lucide-react';
import { RodapeCatalogo } from './RodapeCatalogo';
import { FlyDoDiaBanner } from './FlyDoDiaBanner';
import { pingWhatsapp } from './metrica';

type Produto = {
  id: number;
  codigo_interno: string | null;
  descricao_comercial: string;
  preco_pix: string;
  foto_url: string;
  disponivel: boolean;
  grupo_id: number | null;
  grupo_nome: string | null;
};

type Grupo = { id: number; nome: string; total: number };

function formatBRL(v: string | number): string {
  return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function CatalogoPublicoLista() {
  const [params, setParams] = useSearchParams();
  const grupoId = params.get('grupo') ? Number(params.get('grupo')) : null;
  const q = params.get('q') ?? '';

  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [config, setConfig] = useState<{ whatsapp_link_base: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState(q);

  useEffect(() => {
    document.title = 'Catalogo — FIODECOBRE';
    fetch('/api/public/catalogo/grupos').then((r) => r.json()).then(setGrupos).catch(() => setGrupos([]));
    fetch('/api/public/catalogo/config').then((r) => r.json()).then(setConfig).catch(() => setConfig(null));
  }, []);

  useEffect(() => {
    setLoading(true);
    const url = new URL('/api/public/catalogo', window.location.origin);
    if (grupoId) url.searchParams.set('grupo_id', String(grupoId));
    if (q.trim()) url.searchParams.set('q', q.trim());
    fetch(url.pathname + url.search)
      .then((r) => r.json())
      .then(setProdutos)
      .catch(() => setProdutos([]))
      .finally(() => setLoading(false));
  }, [grupoId, q]);

  const totalGeral = useMemo(() => grupos.reduce((s, g) => s + g.total, 0), [grupos]);

  function aplicarBusca(valor: string) {
    const novo = new URLSearchParams(params);
    if (valor.trim()) novo.set('q', valor.trim());
    else novo.delete('q');
    setParams(novo, { replace: true });
  }

  function trocarGrupo(id: number | null) {
    const novo = new URLSearchParams(params);
    if (id) novo.set('grupo', String(id));
    else novo.delete('grupo');
    setParams(novo, { replace: true });
  }

  return (
    <div className="catalogo-publico-root" style={{ minHeight: '100vh', backgroundColor: '#f7f7f7', color: '#111' }}>
      {/* Header sticky */}
      <header style={{ backgroundColor: '#b45309', color: '#fff' }} className="sticky top-0 z-30 shadow">
        <div className="max-w-5xl mx-auto px-3 py-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-lg sm:text-xl font-bold leading-tight">Fio de Cobre</h1>
              <p className="text-xs opacity-90">Materiais eletricos e hidraulicos</p>
            </div>
            {config?.whatsapp_link_base ? (
              <a
                href={config.whatsapp_link_base}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => pingWhatsapp()}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium"
                style={{ backgroundColor: '#25d366', color: '#fff' }}
              >
                <MessageCircle size={16} /> WhatsApp
              </a>
            ) : null}
          </div>

          {/* Busca */}
          <div className="mt-3 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="search"
              placeholder="Buscar produto..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') aplicarBusca(busca);
              }}
              onBlur={() => aplicarBusca(busca)}
              className="no-upper w-full pl-9 pr-3 py-2 rounded-lg outline-none"
              style={{ backgroundColor: '#fff', color: '#111', textTransform: 'none', fontSize: '16px' }}
            />
          </div>
        </div>

        {/* Chips de departamentos */}
        <div className="max-w-5xl mx-auto px-3 pb-3 overflow-x-auto">
          <div className="flex gap-2 whitespace-nowrap">
            <button
              onClick={() => trocarGrupo(null)}
              className="px-3 py-1 rounded-full text-xs font-medium border"
              style={{
                backgroundColor: grupoId === null ? '#fff' : 'rgba(255,255,255,0.15)',
                color: grupoId === null ? '#b45309' : '#fff',
                borderColor: grupoId === null ? '#fff' : 'rgba(255,255,255,0.3)',
              }}
            >
              Todos ({totalGeral})
            </button>
            {grupos.map((g) => (
              <button
                key={g.id}
                onClick={() => trocarGrupo(g.id)}
                className="px-3 py-1 rounded-full text-xs font-medium border"
                style={{
                  backgroundColor: grupoId === g.id ? '#fff' : 'rgba(255,255,255,0.15)',
                  color: grupoId === g.id ? '#b45309' : '#fff',
                  borderColor: grupoId === g.id ? '#fff' : 'rgba(255,255,255,0.3)',
                }}
              >
                {g.nome} ({g.total})
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Grid de produtos */}
      <main className="max-w-5xl mx-auto px-3 py-4">
        <FlyDoDiaBanner />
        {loading ? (
          <div className="text-center text-gray-500 py-12">Carregando...</div>
        ) : produtos.length === 0 ? (
          <div className="text-center text-gray-500 py-12">
            {q ? `Nenhum produto encontrado pra "${q}".` : 'Nenhum produto disponivel no momento.'}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {produtos.map((p) => (
              <Link
                key={p.id}
                to={`/p/${p.id}`}
                className="rounded-lg overflow-hidden shadow-sm border border-gray-200 hover:shadow-md transition"
                style={{ backgroundColor: '#fff', color: '#111', textDecoration: 'none' }}
              >
                <div className="aspect-square bg-gray-100 relative">
                  <img
                    src={p.foto_url}
                    alt={p.descricao_comercial}
                    loading="lazy"
                    className="w-full h-full object-contain"
                    style={!p.disponivel ? { filter: 'grayscale(1)', opacity: 0.55 } : undefined}
                  />
                  {!p.disponivel && (
                    <span
                      className="absolute top-2 left-2 px-2 py-0.5 text-xs font-bold rounded uppercase tracking-wide"
                      style={{ backgroundColor: '#374151', color: '#fff' }}
                    >
                      Esgotado
                    </span>
                  )}
                </div>
                <div className="p-2">
                  <h2 className="text-sm font-medium leading-tight line-clamp-2 mb-1" style={{ minHeight: '2.5em' }}>
                    {p.descricao_comercial}
                  </h2>
                  {p.disponivel ? (
                    <>
                      <div className="text-base font-bold" style={{ color: '#b45309' }}>
                        R$ {formatBRL(p.preco_pix)}
                      </div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide">Pix ou dinheiro</div>
                    </>
                  ) : (
                    <div className="text-sm font-semibold text-gray-500">Indisponivel no momento</div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <RodapeCatalogo />
    </div>
  );
}
